import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {ACR_MODEL, MIN_MAX_ACR} from '../Model/Models';
import HandleChange from '../../../../../Helpers/HandleChange';
import {
    AdvanceSearchByFilters,
    CreateHidAcr,
    GetAcrInputsNormallyCloseBySio, GetAcrInputsNormallyOpenBySio,
    GetAcrOutputsAvailableBySio,
    GetAcrReadersAvailableBySio,
    GetAcrZonesAvailable
} from "../../../../../reduxToolkit/HID/HidApi";
import { toast } from "react-toastify";
import {errorMsg, isNumber} from "../../../../../Apis/functions";
import { useDispatch, useSelector } from "react-redux";
import { TABLES } from "../../../../../Apis/Tables";


/**
 * @author Karen Yunuen Pozos Arias
 * @position Full Stack Developer - IBL
 * @contact kpozos@ibl.mx
 *
 * @description
 * Custom React hook that manages the creation of ACR (Access Control Reader) entities.
 * Handles form state, input validation, and API communication with Redux actions.
 *
 * @param {Function} onHide - Function to close the modal or current view.
 * @param {number|null} controllerId - Optional controller ID used to pre-load configuration data.
 * @param {Function} setModified - Callback to flag parent component that a record was modified.
 *
 * @returns {Object} - Set of states and handlers for managing ACR creation.
 */
const CreateAcrApp = ({ onHide, controllerId = null, setModified }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { advanceSearchByFilters } = useSelector((state) => state.HidSlice);
    const { getHidRecordDetails } = useSelector((state) => state?.HidSlice);
    const getZones = useSelector((state) => state.HidSlice.getAcrZonesAvailable);

    const [acr, setAcr] = useState({ ...ACR_MODEL, controllerId });
    const { handleChange } = HandleChange({ model: acr, setModel: setAcr });

    const [emptyFields, setEmptyFields] = useState(false);
    const [readerEntryOptions, setReaderEntryOptions] = useState([]);
    const [readerExitOptions, setReaderExitOptions] = useState([]);
    const [outputOptions, setOutputOptions] = useState([]);
    const [sioOptions, setSioOptions] = useState([]);
    const [doorInputsOptions, setDoorInputsOptions] = useState([]);
    const [inputsNormallyOpenOptions, setInputsNormallyOpenOptions] = useState([]);
    const [isValidDoorHeldOpenDelay, setIsValidDoorHeldOpenDelay] = useState(true);
    /**
     * Loads available controllers from the backend
     * when no controller ID is provided.
     */
    useEffect(() => {
        if (!controllerId) {
            dispatch(AdvanceSearchByFilters({ option: TABLES.HID, body: {} }));
        }
    }, [controllerId]);

    /**
     * Loads related SIO and peripheral configuration data
     * whenever a controller or SIO is selected or updated.
     */
    useEffect(() => {
        if (!controllerId && acr.controllerId && advanceSearchByFilters?.length > 0) {
           advanceSearchByFilters.forEach(c => {
                if (c.id === acr.controllerId) {
                    loadFieldOptions(c);
                }
            });
            return;
        }

        if (controllerId && getHidRecordDetails) loadFieldOptions(getHidRecordDetails);
    }, [acr.controllerId, acr.hidSioId, advanceSearchByFilters, getHidRecordDetails]);

    /**
     * Loads SIO-related field options such as zones, readers, inputs, and outputs.
     *
     * @param {Object} data - Controller data containing available SIOs and configurations.
     */
    const loadFieldOptions = (data) => {
        setSioOptions(data.sios);
        if (acr.hidSioId) {
            data.sios.forEach(s => {
                if (s.id === acr.hidSioId) {
                    dispatch(GetAcrZonesAvailable(acr.hidSioId));
                    dispatch(GetAcrReadersAvailableBySio({ hidSioId: acr.hidSioId, isEntry: true }))
                        .then(async ({ payload: { data: { data } } }) => setReaderEntryOptions(data));
                    dispatch(GetAcrReadersAvailableBySio({ hidSioId: acr.hidSioId, isEntry: false }))
                        .then(async ({ payload: { data: { data } } }) => setReaderExitOptions(data));
                    dispatch(GetAcrOutputsAvailableBySio(acr.hidSioId))
                        .then(async ({ payload: { data: { data } } }) => setOutputOptions(data));
                    dispatch(GetAcrInputsNormallyCloseBySio(acr.hidSioId))
                        .then(async ({ payload: { data: { data } } }) => setDoorInputsOptions(data));
                    dispatch(GetAcrInputsNormallyOpenBySio(acr.hidSioId))
                        .then(async ({ payload: { data: { data } } }) => setInputsNormallyOpenOptions(data));
                }
            });
        }
    };

    /**
     * Resets exit-related fields when exit configuration is disabled.
     */
    useEffect(() => {
        if (!acr.enableExitConfig) {
            setAcr(prev => ({ ...prev, exitReaderId: null, exitOutputId: null, exitInputId: null }));
        }
    }, [acr.enableExitConfig]);

    /**
     * Validates and submits the ACR form.
     * If valid, it dispatches the creation API call.
     * On success, shows a toast, resets the form, and closes the modal.
     */
    const handleSubmit = () => {
        if (!acr.isValid() || !isValidDoorHeldOpenDelay) {
            setEmptyFields(true);
            return;
        }

        const body = acr.toAddEdit();
        dispatch(CreateHidAcr(body)).then(({ payload: { data: { success, code } } }) => {
            if (success === true) {
                toast.success(t('has_been_created_successfully'));
                resetValues();
                setModified(true);
                onHide();
            } else {
                errorMsg(
                    code,
                    {
                        zoneName: getZones.filter(e => e.id === acr.zoneId)?.[0]?.name,
                        readerName: readerEntryOptions.filter(e => e.id === acr.entryReaderId)?.[0]?.name ||
                            readerExitOptions.filter(e => e.id === acr.exitReaderId)?.[0]?.name,
                        outputName: outputOptions.filter(e => e.id === acr.entryOutputId)?.[0]?.name
                    },
                    { bold: <strong /> }
                );
            }
        });
    };

    /**
     * Resets the ACR form to its initial state.
     */
    const resetValues = () => {
        setAcr({ ...ACR_MODEL });
    };

    /**
     * Validates that the door held open delay is numeric and within the allowed range.
     */
    useEffect(() => {
        if (acr.doorHeldOpenDelay !== null && acr.doorHeldOpenDelay !== "") {
            const doorHeldOpenDelay = parseInt(acr.doorHeldOpenDelay, 10);
            if (isNumber(doorHeldOpenDelay) && doorHeldOpenDelay > MIN_MAX_ACR.DOOR_HELD_OPEN_DELAY.start && doorHeldOpenDelay <= MIN_MAX_ACR.DOOR_HELD_OPEN_DELAY.end) {
                setIsValidDoorHeldOpenDelay(true);
                return;
            }
            setIsValidDoorHeldOpenDelay(false);
        }
    }, [acr.doorHeldOpenDelay]);

    return {
        t,
        acr,
        handleChange,
        emptyFields,
        controllers: advanceSearchByFilters,
        getZones,
        sioOptions,
        readerEntryOptions,
        readerExitOptions,
        outputOptions,
        doorInputsOptions,
        inputsNormallyOpenOptions,
        isValidDoorHeldOpenDelay,
        handleSubmit,
        resetValues
    };
};

export default CreateAcrApp;
